import { useEffect, useState, useCallback } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { EmptyState, InfoCard } from '@backstage/core-components';
import {
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  makeStyles,
  createStyles,
  Theme,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import DescriptionIcon from '@material-ui/icons/Description';
import RefreshIcon from '@material-ui/icons/Refresh';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface DocFile {
  name: string;
  path: string;
  size: number;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fileList: {
      borderRight: `1px solid ${theme.palette.divider}`,
      minHeight: 400,
    },
    selectedFile: {
      backgroundColor: theme.palette.action.selected,
    },
    markdownContainer: {
      padding: theme.spacing(2),
      '& h1': {
        ...theme.typography.h4,
        borderBottom: `1px solid ${theme.palette.divider}`,
        paddingBottom: theme.spacing(1),
        marginBottom: theme.spacing(2),
      },
      '& h2': {
        ...theme.typography.h5,
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(1),
      },
      '& h3': {
        ...theme.typography.h6,
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
      },
      '& table': {
        borderCollapse: 'collapse',
        width: '100%',
        marginBottom: theme.spacing(2),
      },
      '& th, & td': {
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(1),
        textAlign: 'left',
      },
      '& th': {
        backgroundColor: theme.palette.background.default,
        fontWeight: theme.typography.fontWeightBold,
      },
      '& code': {
        backgroundColor: theme.palette.background.default,
        padding: '2px 6px',
        borderRadius: 4,
        fontSize: '0.875em',
        fontFamily: 'monospace',
      },
      '& pre': {
        backgroundColor: theme.palette.background.default,
        padding: theme.spacing(2),
        borderRadius: 4,
        overflow: 'auto',
        '& code': {
          padding: 0,
          backgroundColor: 'transparent',
        },
      },
      '& blockquote': {
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        margin: 0,
        paddingLeft: theme.spacing(2),
        color: theme.palette.text.secondary,
      },
      '& a': {
        color: theme.palette.primary.main,
      },
      '& img': {
        maxWidth: '100%',
      },
      '& ul, & ol': {
        paddingLeft: theme.spacing(3),
      },
      '& hr': {
        border: 'none',
        borderTop: `1px solid ${theme.palette.divider}`,
        margin: theme.spacing(2, 0),
      },
    },
  }),
);

function formatFileName(name: string): string {
  return name
    .replace(/\.md$/i, '')
    .replace(/_/g, ' ')
    .replace(/-/g, ' ');
}

export function DocsViewerContent() {
  const classes = useStyles();
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch: fetchApi } = useApi(fetchApiRef);

  const [files, setFiles] = useState<DocFile[]>([]);
  // ?file= abre ese archivo (enlaces de la búsqueda en docs).
  const [selectedFile, setSelectedFile] = useState<string | null>(() => new URLSearchParams(window.location.search).get('file'));
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [loadingContent, setLoadingContent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const slug = entity.metadata.annotations?.['github.com/project-slug'] ?? '';
  const [owner, repo] = slug.split('/');

  const fetchFiles = useCallback(async () => {
    if (!owner || !repo) return;
    setLoading(true);
    setError(null);
    setNotFound(false);

    try {
      const baseUrl = await discoveryApi.getBaseUrl('docs-viewer');
      const response = await fetchApi(`${baseUrl}/files/${owner}/${repo}`);

      if (response.status === 404) {
        setNotFound(true);
        setFiles([]);
        return;
      }

      if (!response.ok) {
        throw new Error(`Error ${response.status}`);
      }

      const data = (await response.json()) as DocFile[];
      setFiles(data);

      if (data.length > 0 && !selectedFile) {
        setSelectedFile(data[0].name);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, fetchApi, owner, repo, selectedFile]);

  const fetchContent = useCallback(async (filename: string) => {
    if (!owner || !repo) return;
    setLoadingContent(true);

    try {
      const baseUrl = await discoveryApi.getBaseUrl('docs-viewer');
      const response = await fetchApi(`${baseUrl}/content/${owner}/${repo}/${filename}`);

      if (!response.ok) {
        throw new Error(`Error ${response.status}`);
      }

      const data = (await response.json()) as { filename: string; content: string };
      setContent(data.content);
    } catch (err: any) {
      setContent(`> Error loading ${filename}: ${err.message}`);
    } finally {
      setLoadingContent(false);
    }
  }, [discoveryApi, fetchApi, owner, repo]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  useEffect(() => {
    if (selectedFile) {
      fetchContent(selectedFile);
    }
  }, [selectedFile, fetchContent]);

  if (!slug) {
    return (
      <EmptyState
        title="No GitHub annotation"
        description="This entity does not have a github.com/project-slug annotation."
        missing="info"
      />
    );
  }

  if (loading) {
    return <LinearProgress />;
  }

  if (error) {
    return (
      <InfoCard title="Error">
        <Typography color="error">{error}</Typography>
      </InfoCard>
    );
  }

  if (notFound) {
    return (
      <EmptyState
        title="No documentation found"
        description={`The repository ${slug} does not have a .docs/ folder.`}
        missing="info"
      />
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <InfoCard
          title="Files"
          action={
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={() => { setSelectedFile(null); fetchFiles(); }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          }
        >
          <List dense className={classes.fileList}>
            {files.map(file => (
              <ListItem
                button
                key={file.name}
                selected={selectedFile === file.name}
                onClick={() => setSelectedFile(file.name)}
                className={selectedFile === file.name ? classes.selectedFile : undefined}
              >
                <ListItemIcon>
                  <DescriptionIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={formatFileName(file.name)} />
              </ListItem>
            ))}
          </List>
        </InfoCard>
      </Grid>
      <Grid item xs={12} md={9}>
        <InfoCard title={selectedFile ? formatFileName(selectedFile) : 'Select a file'}>
          {loadingContent ? (
            <LinearProgress />
          ) : (
            <div className={classes.markdownContainer}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {content}
              </ReactMarkdown>
            </div>
          )}
        </InfoCard>
      </Grid>
    </Grid>
  );
}
